import '@operato/input/ox-input-search.js'
import './ox-board-creation-card.js'

import gql from 'graphql-tag'
import { css, html, LitElement, PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { client, gqlContext } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { ScrollbarStyles } from '@operato/styles'
import InfiniteScrollable from '@operato/utils/mixins/infinite-scrollable.js'
import { InheritedValueType } from '@operato/shell'
import { adjustFilters } from '@operato/utils'

const FETCH_BOARD_LIST_GQL = gql`
  query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!], $inherited: InheritedValueType) {
    boards(filters: $filters, pagination: $pagination, sortings: $sortings, inherited: $inherited) {
      items {
        id
        name
        description
        thumbnail
      }
      total
    }
  }
`

const FETCH_BOARD_GROUP_LIST_GQL = gql`
  {
    groups {
      items {
        id
        name
        description
      }
      total
    }
  }
`

const CREATE_BOARD_GQL = gql`
  mutation CreateBoard($board: NewBoard!) {
    createBoard(board: $board) {
      id
      name
      description
      model
      createdAt
      updatedAt
    }
  }
`

@customElement('ox-board-selector')
export class BoardSelector extends InfiniteScrollable(localize(i18next)(LitElement)) {
  static get styles() {
    return [
      ScrollbarStyles,
      css`
        :host {
          display: grid;
          grid-template-rows: auto auto 1fr;
          overflow: hidden;
          color: var(--md-sys-color-on-surface);
          background-color: var(--md-sys-color-surface);
        }

        #main {
          overflow: auto;
          padding: var(--popup-content-padding);
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          grid-auto-rows: var(--card-list-rows-height);
          grid-gap: 20px;
        }

        .card {
          display: flex;
          flex-direction: column;
          align-items: center;
          overflow: hidden;
          border-radius: var(--card-list-border-radius);
          background-color: var(--card-list-background-color);
        }

        .card[selected] {
          border: red solid;
        }

        .card.create {
          overflow: visible;
        }

        .card:hover {
          cursor: pointer;
        }

        .card > .name {
          color: var(--md-sys-color-on-secondary);
          background-color: var(--md-sys-color-secondary);
          opacity: 0.9;
          margin-top: -35px;
          width: 100%;
          font-weight: bolder;
          font-size: 13px;
          text-indent: 7px;
        }

        .card > .description {
          color: var(--md-sys-color-on-secondary);
          background-color: var(--md-sys-color-secondary);
          width: 100%;
          min-height: 16px;
          font-size: 0.6rem;
          text-indent: 7px;
        }
        .card img {
          max-height: 100%;
          min-height: 100%;
        }

        #filter {
          display: flex;
          align-items: center;
          padding: var(--popup-content-padding);
          color: var(--md-sys-color-on-surface-variant);
          background-color: var(--md-sys-color-surface-variant);
          box-shadow: var(--box-shadow);
        }

        #filter > ox-input-search {
          margin-right: auto;
          border: var(--md-sys-color-primary) 1px solid;
          padding: var(--spacing-small) var(--spacing-small);
          border-radius: 999px;
          color: var(--md-sys-color-primary);
        }

        #filter > div {
          float: right;
          margin-left: 10px;
        }

        #filter * {
          font-size: 15px;
        }

        select {
          text-transform: capitalize;
        }
      `
    ]
  }

  @property({ type: Array }) groups: { id: string; name: string; description: string }[] = []
  @property({ type: Array }) boards: any[] = []
  @property({ type: String }) group?: string
  @property({ type: String }) search?: string
  @property({ type: String }) inherited?: InheritedValueType = InheritedValueType.Include
  @property({ type: Boolean }) creatable: boolean = false
  @property({ type: String }) value?: string

  private _page: number = 1
  private _total: number = 0

  constructor() {
    super()

    this._infiniteScrollOptions.limit = 50
  }

  render() {
    return html`
      <div id="filter">
        <ox-input-search
          @change=${(e: Event) => {
            this.search = (e.currentTarget as any)?.value
          }}
        ></ox-input-search>
        <div>
          <label for="group">Group</label>
          <select
            id="group"
            @change=${(e: Event) => {
              this.group = (e.currentTarget as any)?.value
              this.requestUpdate()
            }}
          >
            <option value="">${i18next.t('label.all')}</option>
            ${this.groups.map(group => html` <option value=${group.id}>${group.description}</option> `)}
          </select>
        </div>

        <div>
          <label for="inherited">Inherited</label>
          <select
            id="inherited"
            @change=${(e: Event) => {
              this.inherited = (e.currentTarget as any)?.value
              this.requestUpdate()
            }}
          >
            <option value="Include">Include</option>
            <option value="Only">Only</option>
            <option value="None">None</option>
          </select>
        </div>
      </div>

      <div
        id="main"
        @scroll=${(e: Event) => {
          this.onScroll(e)
        }}
      >
        ${this.creatable
          ? html`
              <ox-board-creation-card
                class="card create"
                .groups=${this.groups}
                .defaultGroup=${this.group}
                @create-board=${(e: CustomEvent) => this.onCreateBoard(e)}
              ></ox-board-creation-card>
            `
          : html``}
        ${this.boards.map(
          board => html`
            <div class="card" @click=${(e: Event) => this.onClickSelect(board)} ?selected=${this.value === board.id}>
              <img src=${board.thumbnail} />
              <div class="name">${board.name}</div>
              <div class="description">${board.description}</div>
            </div>
          `
        )}
      </div>
    `
  }

  get scrollTargetEl(): HTMLElement | null {
    return this.renderRoot.querySelector('#main') as HTMLElement
  }

  async scrollAction() {
    return this.appendBoards()
  }

  firstUpdated() {
    this.refreshGroups()
  }

  async updated(changed: PropertyValues<this>) {
    if (changed.has('group') || changed.has('inherited') || changed.has('search')) {
      this.refreshBoards()
    }
  }

  onClickSelect(board: any) {
    this.dispatchEvent(
      new CustomEvent('board-selected', {
        composed: true,
        bubbles: true,
        detail: {
          board
        }
      })
    )
  }

  async onCreateBoard(e: CustomEvent) {
    var { name, description, groupId, model, thumbnail } = e.detail

    await this.createBoard(name, description, groupId, model, thumbnail)
    this.refreshBoards()
  }

  async refreshGroups() {
    var groupListResponse = await client.query({
      query: FETCH_BOARD_GROUP_LIST_GQL,
      context: gqlContext()
    })

    if (!groupListResponse || !groupListResponse.data) return

    var groups = groupListResponse.data.groups.items
    this.groups = [...groups]

    this.group = groups.filter((group: { id: string }) => group.id == this.group).length > 0 ? this.group : ''
  }

  async refreshBoards() {
    var boards = await this.getBoards()
    this.boards = [...boards]

    var creationCard = this.renderRoot.querySelector('ox-board-creation-card')
    if (creationCard) {
      ;(creationCard as any).reset()
    }
  }

  async appendBoards() {
    var boards = await this.getBoards({ page: this._page + 1 })
    this.boards = [...this.boards, ...boards]
  }

  async getBoards({ page = 1, limit = this._infiniteScrollOptions.limit } = {}) {
    var filters = [] as any[]
    var sortings = [] as any[]
    var pagination = {
      limit,
      page
    }

    if (this.group) {
      filters.push({
        name: 'groupId',
        operator: 'eq',
        value: this.group
      })
    }

    if (this.search) {
      const value = `%${this.search.trim()}%`

      filters.push({
        name: 'name',
        operator: 'search',
        value: this.search
      })

      filters.push({
        name: 'description',
        operator: 'search',
        value: this.search
      })
    }

    var variables = {
      filters,
      sortings,
      pagination,
      inherited: this.inherited || InheritedValueType.Include
    }

    var boardListResponse = await client.query({
      query: FETCH_BOARD_LIST_GQL,
      variables
    })

    if (!boardListResponse || !boardListResponse.data) return []
    this._total = boardListResponse.data.boards.total
    this._page = page

    return boardListResponse.data.boards.items
  }

  async createBoard(name: string, description: string, groupId: string, modelTemplate: any, thumbnail: string) {
    var model = JSON.stringify(
      modelTemplate || {
        width: 1200,
        height: 800,
        fillStyle: 'white'
      }
    )

    const response = await client.mutate({
      mutation: CREATE_BOARD_GQL,
      variables: {
        board: {
          name,
          description,
          groupId,
          model,
          thumbnail
        }
      },
      context: gqlContext()
    })

    return response.data
  }
}
